<template>
    <div v-if="details" class="subscriber-account-root">
        <div class="d-flex pt-2 p-1">
            <div class="subscriber-title mb-0">
                {{ userType }}: {{ details.client.user.email }}
                <span v-if="!isSubscriber"
                    >(<a :href="parentHref">Subscriber: {{ details.client.user.parent_id }}</a
                    >)</span
                >
                <span @click="$bvModal.show(loginAsUserModalId)">
                    <font-awesome-icon class="log-in-icon" title="Login" :icon="['fas', 'user']" />
                </span>
                <span @click="$bvModal.show(loginAsUserModalIdMobile)">
                    <font-awesome-icon
                        class="log-in-icon"
                        title="App Login As..."
                        :icon="['fas', 'mobile-screen-button']"
                    />
                </span>
            </div>
        </div>
        <page :busy="busy" :tabs="tabs">
            <router-view @busy="handleBusy"></router-view>
        </page>
        <modal :id="loginAsUserModalId" title="Login as user" @ok="loginAsUser">
            Are you sure you want to log in as
            {{ details ? details.client.user.email : '' }}?
        </modal>
        <modal :id="loginAsUserModalIdMobile" title="Login as user on the mobile app" @ok="loginAsUserMobile">
            Are you sure you want to log in as
            {{ details ? details.client.user.email : '' }} on the mobile app?
        </modal>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import Page from '../../generic/page/page.vue';
import Modal from '../../generic/modal/modal.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';

export default {
    name: 'SubscriberAccount',
    mixins: [removeScrollMixin],
    components: {
        Page,
        ResponsiveButton,
        Modal,
    },
    mounted() {
        this.fetchAllData();
    },
    provide() {
        return {
            userId: this.userId,
        };
    },
    computed: {
        ...mapGetters('adminSubscriberAccount', ['details']),
        loginAsUserModalId() {
            return 'login-as-user-modal';
        },
        loginAsUserModalIdMobile() {
            return 'login-as-user-modal-mobile';
        },
        userId() {
            return parseInt(this.$route.params.id);
        },
        isSubscriber() {
            return this.details && this.details.client.is_subscriber;
        },
        parentHref() {
            return this.details && `/app/admin/user/details/${this.details.client.user.parent_id}`;
        },
        userType() {
            if (this.details && this.details.client) {
                if (this.details.client.is_subscriber) {
                    return 'Subscriber';
                } else if (this.details.client.is_team_member) {
                    return 'Team Member';
                }
                if (this.details.client.is_owner) {
                    return 'Owner';
                }
            }
            return '';
        },
        tabs() {
            if (this.details && this.details.client.is_subscriber) {
                return [
                    {
                        title: `Details`,
                        route: `/app/admin/user/details/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/details/'),
                    },
                    {
                        title: `Subscription Details`,
                        route: `/app/admin/user/subscription-details/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/subscription-details/'),
                    },
                    {
                        title: `Subscription History`,
                        route: `/app/admin/user/subscription-history/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/subscription-history/'),
                    },
                    {
                        title: `Payments`,
                        route: `/app/admin/user/payments/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/payments/'),
                    },
                    {
                        title: `Users`,
                        route: `/app/admin/user/users/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/users/'),
                    },
                    {
                        title: `Communication`,
                        route: `/app/admin/user/communication/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/communication/'),
                    },
                    {
                        title: `Billing Data`,
                        route: `/app/admin/user/billing-data/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/billing-data/'),
                    },
                    {
                        title: `Comments`,
                        route: `/app/admin/user/comments/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/comments/'),
                    },
                    {
                        title: `Damage Protection`,
                        route: `/app/admin/user/damage-protection/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/damage-protection/'),
                    },
                ];
            } else {
                return [
                    {
                        title: `Details`,
                        route: `/app/admin/user/details/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/details/'),
                    },
                    {
                        title: `Communication`,
                        route: `/app/admin/user/communication/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/communication/'),
                    },
                    {
                        title: `Comments`,
                        route: `/app/admin/user/comments/${this.userId}`,
                        active: this.$route.path.includes('/app/admin/user/comments/'),
                    },
                ];
            }
        },
    },
    data() {
        return {
            busy: false,
        };
    },
    methods: {
        ...mapActions('adminSubscriberAccount', ['fetchDetails', 'fetchCountries']),
        handleBusy(busy) {
            this.busy = busy;
        },
        loginAsUser() {
            window.location = `/administrative/login_as_user/${this.userId}`;
        },
        loginAsUserMobile() {
            window.location = `/administrative/login_as_user/${this.userId}?redirect_to_mobile_app=true`;
        },
        async fetchAllData() {
            this.handleBusy(true);
            await this.fetchAccountDetails();
            await this.fetchCountryList();
            this.handleBusy(false);
        },
        async fetchCountryList() {
            try {
                this.handleBusy(true);
                await this.fetchCountries();
            } catch (e) {
                this.errorToast({ text: 'Could not fetch country list' });
            }
        },
        async fetchAccountDetails() {
            try {
                await this.fetchDetails({
                    id: this.userId,
                });
            } catch (e) {
                this.errorToast({ text: 'Could not fetch account details' });
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.subscriber-account-root {
    height: calc(100% - 63px);
    position: relative;
    padding: 0 0 45px 15px;
}
.log-in-icon {
    padding: 5px;
    border-radius: 10%;
    width: 30px;
    height: 30px;
    cursor: pointer;
    &:hover {
        background: #cdcdcd;
    }
}
.subscriber-title {
    font-weight: bold;
    font-size: 1.25rem;
}

@include media-breakpoint-down(md) {
    .subscriber-title {
        font-size: 1rem;
    }
}
</style>